/**
 * Coach-mark tour definitions.
 *
 * Each tour is a short, ordered list of steps anchored to real UI elements
 * via a `[data-tour="..."]` attribute (CSS-selector anchoring — the element
 * tags itself, the tour finds it). Keep tours to ~3 steps and only ever
 * point at things that answer "what do I DO here"; "what is this view" is the
 * OnboardingModal's job.
 *
 * IMPORTANT — anchor liveness: every `target` in a tour must be present in
 * the DOM *at the same time*. Elements that only render on a later phase
 * (e.g. a per-door outcome button that needs a live shift) cannot be part of
 * a single linear tour; those become a separate, progressive tour that
 * advances as the user navigates. This first slice deliberately anchors only
 * to the Canvass *home* screen, where all three targets co-exist.
 *
 * Tour ids are versioned (`-v1`). Bump the version when a flow changes enough
 * that veterans should see the refreshed guide again — a new id is absent
 * from their `onboarding_state.seen`, so it re-shows.
 */

export type TourPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface TourStep {
  /** CSS selector for the anchor, e.g. `[data-tour="new-shift"]`. */
  target: string;
  /** Short imperative headline — "Start here", not "The shift button". */
  title: string;
  /** One or two sentences on what to do. */
  body: string;
  /** Preferred side to place the popover on desktop. Default 'bottom'. */
  placement?: TourPlacement;
}

export interface Tour {
  /** Versioned id stored in `onboarding_state.seen`. */
  id: string;
  steps: TourStep[];
}

/**
 * Canvasser — first landing on the Door Canvass home screen. Fires *after*
 * the CanvassView OnboardingModal is dismissed (explain → then guide).
 */
export const CANVASSER_HOME_TOUR: Tour = {
  id: 'canvasser-home-v1',
  steps: [
    {
      target: '[data-tour="new-shift"]',
      title: 'Start here',
      body: 'Tap “New shift” to pick a route and start knocking. You can spin up a walk-up shift right in the field — no pre-planning needed.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="recent-shifts"]',
      title: 'Pick up where you left off',
      body: 'Shifts you’ve already started show up here. Tap Resume on any open one instead of starting over.',
      placement: 'top',
    },
    {
      target: '[data-tour="onboarding-help"]',
      title: 'Need a refresher?',
      body: 'Tap Help any time to reopen the “what is this view” explainer. This quick tour won’t nag you again once you’re done.',
      placement: 'left',
    },
  ],
};

/**
 * Canvasser — the 'stops' phase, first time a live shift's door list loads.
 * This is the "progressive" continuation of the home tour: rather than one
 * linear tour spanning phases (whose later anchors don't exist yet), each
 * phase gets its own short tour that fires when the canvasser first reaches
 * it — advance-as-you-walk, with every anchor guaranteed live.
 */
export const CANVASSER_STOPS_TOUR: Tour = {
  id: 'canvasser-stops-v1',
  steps: [
    {
      target: '[data-tour="stops-progress"]',
      title: 'You’re knocking',
      body: 'Your doors, in walking order. Tap any door — on the map or in the list — to open its card and log what happened.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="stops-end"]',
      title: 'Done for the day?',
      body: 'Tap End shift to close out and file a quick after-action report. Everything you logged is already saved.',
      placement: 'left',
    },
  ],
};

/**
 * Canvasser — the 'card' phase, first time a per-door card opens. One step:
 * the single most important action a canvasser takes.
 */
export const CANVASSER_CARD_TOUR: Tour = {
  id: 'canvasser-card-v1',
  steps: [
    {
      target: '[data-tour="card-outcomes"]',
      title: 'Log the outcome',
      body: 'Tap what happened at this door. Choose “Talked” to record who answered and how they lean.',
      placement: 'top',
    },
  ],
};

/**
 * Canvass AAR Inbox — the review queue org managers / coordinators work.
 * View-scoped rather than role-scoped on purpose: several roles (org_manager,
 * manager, coordinator, admin) reach this view, and every one of them has the
 * same "what do I do here" question the first time. Fires after this view's
 * OnboardingModal, mirroring the Canvass handoff.
 */
export const CANVASS_REVIEW_TOUR: Tour = {
  id: 'canvass-review-v1',
  steps: [
    {
      target: '[data-tour="review-inbox"]',
      title: 'Your review queue',
      body: 'Each row below is an after-action report a canvasser filed at the end of a shift. Open one, skim the auto-summary, then approve to clear it.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="review-field-devices"]',
      title: 'Watch the field',
      body: 'Check here first. An amber queued count means knocks are stuck offline; a stale device usually means a canvasser’s phone died mid-shift.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="onboarding-help"]',
      title: 'Need the details?',
      body: 'Tap Help any time to reopen the full explainer for this view. This quick tour won’t nag you again once you’re done.',
      placement: 'left',
    },
  ],
};

/**
 * Coverage — the turf-planning surface (org managers, coordinators, analysts).
 * Ordered as the actual job reads: narrow to your turf → find the gaps → pull
 * fresh numbers. Anchored only to controls that render before data loads (the
 * DataTable/EmptyState below is behind a loading ternary, so it's not tour-safe).
 */
export const COVERAGE_TOUR: Tour = {
  id: 'coverage-v1',
  steps: [
    {
      target: '[data-tour="coverage-filters"]',
      title: 'Zero in on your turf',
      body: 'Filter by precinct, subregion, or legislative district to see just the area you manage. Flip “Under-contacted only” to surface the precincts falling behind their target — that’s where to send canvassers next.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="coverage-refresh"]',
      title: 'Pull the latest numbers',
      body: 'Coverage recomputes as canvassers file their shifts. Hit Refresh to pull the newest contact rates before you plan turf.',
      placement: 'left',
    },
    {
      target: '[data-tour="onboarding-help"]',
      title: 'Need the details?',
      body: 'Tap Help any time to reopen the full explainer for this view. This quick tour won’t nag you again once you’re done.',
      placement: 'left',
    },
  ],
};

/**
 * County Command Center (MapView) — the landing view for every analytics-
 * capable role, so this tour has the widest reach of any.
 *
 * Teaches the app's core thesis: every number is a doorway. The host gates
 * activation on the county data having loaded — MapView's whole body is behind
 * a `stats && ldStats && precincts` guard, and roles without analytics access
 * (e.g. CANVASSER) get a 403 and see only a header + error banner. Without that
 * gate the tour would poll for anchors that are never coming.
 */
export const MAP_TOUR: Tour = {
  id: 'map-v1',
  steps: [
    {
      target: '[data-tour="map-ld-cards"]',
      title: 'Every number is a doorway',
      body: 'Click any Legislative District to drill into its precincts, turnout history, and voters. Nothing on this screen is a dead end.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="map-subregions"]',
      title: 'Down to walkable turf',
      body: 'Subregions are the turf units canvassers actually walk. Click one to see who lives there and how they vote — or sort to find the turf worth working first.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="onboarding-help"]',
      title: 'Need the details?',
      body: 'Tap Help any time to reopen the full explainer for this view. This quick tour won’t nag you again once you’re done.',
      placement: 'left',
    },
  ],
};

/** Registry — keyed by tour id so a host can look one up. */
export const TOURS: Record<string, Tour> = {
  [CANVASSER_HOME_TOUR.id]: CANVASSER_HOME_TOUR,
  [CANVASSER_STOPS_TOUR.id]: CANVASSER_STOPS_TOUR,
  [CANVASSER_CARD_TOUR.id]: CANVASSER_CARD_TOUR,
  [CANVASS_REVIEW_TOUR.id]: CANVASS_REVIEW_TOUR,
  [COVERAGE_TOUR.id]: COVERAGE_TOUR,
  [MAP_TOUR.id]: MAP_TOUR,
};
